import {DataStatus, TabProps, Tabs} from 'platform/components';
import {Show} from 'platform/foundation';

import {isNil} from 'ramda';
import {isEmptyString} from 'ramda-adjunct';

import {GetFastTrackIssueResponse, useGetFastTrackIssueQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {queryParams, Section} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {RequiredTestIdProps, suffixTestId, useBoolean, useQueryState} from 'shared/web';

import {AddMaterialModal} from './components/AddMaterialModal';
import {GeneralInformationTab} from './components/GeneralInformationTab';
import {ListOfItemsTab} from './components/ListOfItemsTab';

interface OverviewProps extends RequiredTestIdProps {}

export function Overview(props: OverviewProps) {
  const {fastTrackIssueId} = useWarehouseParams();

  const [isMaterialModalVisible, openMaterialModal, closeMaterialModal] = useBoolean();

  const [activeTabId, setActiveTabId] = useQueryState(queryParams.COMPONENT_SECTIONS_TAB);

  const fastTrackIssueQuery = useGetFastTrackIssueQuery(
    {fastTrackId: fastTrackIssueId},
    {skip: isNil(fastTrackIssueId) || isEmptyString(fastTrackIssueId)}
  );

  const isAddMaterialDisabled =
    fastTrackIssueQuery.data?.state === 'COMPLETED' ||
    fastTrackIssueQuery.data?.state === 'FAST_TRACK_ISSUE';

  const isFastTrackIssued = fastTrackIssueQuery.data?.state === 'FAST_TRACK_ISSUE';

  const tabs: TabProps[] = [
    {
      id: 'general-information',
      title: i18n.t('general.labels.generalInformation'),
      content: (
        <GeneralInformationTab data-testid={suffixTestId('tabs.generalInformation', props)} />
      ),
    },
    {
      id: 'list-of-items',
      title: i18n.t('general.labels.listOfItems'),
      actions: [
        {
          isDisabled: isAddMaterialDisabled,
          type: 'buttonWithTooltip',
          variant: 'outlined',
          title: i18n.t('general.actions.addMaterial'),
          onClick: openMaterialModal,
          tooltip: {
            isDisabled: !isFastTrackIssued,
            label: i18n.t('entity.warehouse.tooltips.cannotAddMaterialWhenFastTrackIssued'),
          },
          'data-testid': suffixTestId('actions.add', props),
        },
      ],
      content: (
        <ListOfItemsTab
          fastTrackIssueId={fastTrackIssueId}
          data-testid={suffixTestId('tabs.listOfItems', props)}
        />
      ),
    },
  ];

  return (
    <Section isFullHeight data-testid={suffixTestId('wrapper', props)}>
      <DataStatus isLoading={fastTrackIssueQuery.isLoading} isError={fastTrackIssueQuery.isError}>
        <Tabs
          isFullHeight
          isLazy
          variant="condensed"
          tabs={tabs}
          activeTabId={activeTabId}
          onChange={setActiveTabId}
        />
        <Show when={isMaterialModalVisible}>
          <AddMaterialModal
            fastTrack={fastTrackIssueQuery.data as GetFastTrackIssueResponse}
            fastTrackId={fastTrackIssueId}
            onDiscard={closeMaterialModal}
            data-testid={suffixTestId('addMaterialModal', props)}
          />
        </Show>
      </DataStatus>
    </Section>
  );
}
